import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import AgGridDataTable from "../DataTable/AgGrid/AgGridDataTable";
import {
  currentDateTime,
  dateTimeFormat,
  decryptMsgData,
  formatDateWithSeconds,
  randomFiveDigitString,
  stringtoDDMMYYYY,
} from "../../helper/common";
import { newScriptObject } from "../../Static/data";
import FavouriteStar from "../UI/FavouriteStar";
import MarketWatchPriceChangeFontColor from "./MarketWatchPriceChangeFontColor";
import PriceChnageColored from "../UI/PriceChnageColored";

const PRICE_WIDTH = 100;

const DEFAULT_COLUMNS = [
  {
    field: "favourite",
    headerName: "",
    headerTooltip: "Favourite",
    minWidth: 30,
    movable: false,
  },
  {
    field: "exchange",
    headerName: "Exchange",
    headerTooltip: "Exchange",
    cellClass: "color-cell",
    filter: true,
    minWidth: 100,
  },
  {
    field: "name",
    headerName: "Symbol",
    headerTooltip: "Name",
    cellClass: "color-cell",
    sort: true,
    filter: true,
    minWidth: 200,
  },
  {
    field: "expiry",
    headerName: "Expiry",
    headerTooltip: "Expiry",
    cellClass: "color-cell",
    minWidth: 120,
  },
  {
    field: "buy_qty_0",
    headerName: "Buy Qty",
    headerTooltip: "Buy Quantity",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: 80,
    cellRenderer: (p) => <PriceChnageColored value={p.value} isColor={false} />,
  },
  {
    field: "buy_price_0",
    headerName: "Buy Price",
    headerTooltip: "Buy Price",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: PRICE_WIDTH,
    cellRenderer: (p) => <PriceChnageColored value={p.value} />,
  },
  {
    field: "sell_price_0",
    headerName: "Sell Price",
    headerTooltip: "Sell Price",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: PRICE_WIDTH,
    cellRenderer: (p) => <PriceChnageColored value={p.value} />,
  },
  {
    field: "sell_qty_0",
    headerName: "Sell Qty",
    headerTooltip: "Sell Quantity",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: 80,
    cellRenderer: (p) => <PriceChnageColored value={p.value} isColor={false} />,
  },
  {
    field: "net_change",
    headerName: "Net Change",
    headerTooltip: "Net Change",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: 100,
    cellRenderer: (p) => <MarketWatchPriceChangeFontColor value={p.value} />,
  },
  {
    field: "ohlc_open",
    headerName: "Open",
    headerTooltip: "Open",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: PRICE_WIDTH,
  },
  {
    field: "ohlc_high",
    headerName: "High",
    headerTooltip: "High",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "ohlc_low",
    headerName: "Low",
    headerTooltip: "Low",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "ohlc_close",
    headerName: "Close",
    headerTooltip: "Close",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "last_price",
    headerName: "LTP",
    headerTooltip: "Last Trade Price",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "percentage_change",
    headerName: "% change",
    headerTooltip: "Percentage Change",
    headerClass: "text-end justify-content-end",
    cellClass: "color-cell",
    minWidth: 90,
    cellRenderer: (p) => (
      <MarketWatchPriceChangeFontColor value={p.value} isPercentage={true} />
    ),
  },
  {
    field: "timestamp",
    headerName: "lut",
    headerTooltip: "Last Unit Time",
    headerClass: "text-end justify-content-end",
    minWidth: 80,
    cellClass: "text-end color-cell",
    valueFormatter: (params) => formatDateWithSeconds(params.value),
  },
  {
    field: "LastTradeTimeD",
    headerName: "ltt",
    headerTooltip: "Last Trade Time",
    headerClass: "text-end justify-content-end",
    minWidth: 80,
    cellClass: "text-end color-cell",
    valueFormatter: (params) => formatDateWithSeconds(params.value),
  },
  {
    field: "lower_circuit",
    headerName: "LC",
    headerTooltip: "Lower Circuit",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "upper_circuit",
    headerName: "UC",
    headerTooltip: "Upper Circuit",
    headerClass: "text-end justify-content-end",
    minWidth: PRICE_WIDTH,
    cellClass: "text-end color-cell",
  },
  {
    field: "buy_quantity",
    headerName: "tbq",
    headerTooltip: "Total Buy Quantity",
    headerClass: "text-end justify-content-end",
    minWidth: 80,
    cellClass: "text-end color-cell",
  },
  {
    field: "sell_quantity",
    headerName: "tsq",
    headerTooltip: "Total Sell Quantity",
    headerClass: "text-end justify-content-end",
    minWidth: 80,
    cellClass: "text-end color-cell",
  },
  {
    field: "oi",
    headerName: "oi",
    headerTooltip: "Open Interest",
    headerClass: "text-end justify-content-end",
    minWidth: 100,
    cellClass: "text-end color-cell",
  },
  {
    field: "lot_size",
    headerName: "lot size",
    headerTooltip: "Lot Size",
    headerClass: "text-end justify-content-end",
    minWidth: 100,
    cellClass: "text-end color-cell",
  },
  {
    field: "volume",
    headerName: "volume",
    headerTooltip: "Volume",
    headerClass: "text-end justify-content-end",
    minWidth: 120,
    cellClass: "text-end color-cell",
  },
  {
    field: "mode",
    headerName: "mode",
    headerTooltip: "Volume",
    headerClass: "text-end justify-content-end",
    minWidth: 80,
    cellClass: "text-end color-cell",
  },
];

const MarketWatchTable = forwardRef(
  ({ contextRef, useChat, dispatch, marketAction, uiActions }, ref) => {
    const { connected, on, off, send } = useChat;
    const [rowData, setRowData] = useState([]);
    const { canvasOptions } = useSelector((state) => state.ui);
    const {
      marketActiveTab,
      marketWatchScripts,
      marketFilterScript,
      marketExchangeScript,
      marketWatchColumns,
    } = useSelector((state) => state.market);

    const gridRef = useRef();
    const updateQueue = useRef([]);
    const customRowOrderRef = useRef([]);
    const modalRef = useRef(canvasOptions);
    const filterScriptRef = useRef(marketFilterScript);
    const marketWatchScriptsRef = useRef(marketWatchScripts);
    const marketActiveTabRef = useRef(marketActiveTab);
    const marketExchangeScriptRef = useRef(marketExchangeScript);
    const prevTabRef = useRef(null);
    const isFirstUpdateAfterTabChange = useRef(false);

    const { dateYMD, nextTwoMonthsYMD } = currentDateTime();

    useEffect(() => {
      if (!connected) return;
      on("Receive_FullFeed", handleFullFeed);
      on("Receive_UpdatedFeed", handleUpdateFeed);
      return () => {
        off("Receive_FullFeed");
        off("Receive_UpdatedFeed");
        handleUnsubscribe();
      };
    }, [connected]);

    // keep ref updated with latest state
    useEffect(() => {
      modalRef.current = canvasOptions;
    }, [canvasOptions]);

    useEffect(() => {
      filterScriptRef.current = marketFilterScript;
    }, [marketFilterScript]);

    useEffect(() => {
      marketWatchScriptsRef.current = marketWatchScripts;
    }, [marketWatchScripts]);

    useEffect(() => {
      marketExchangeScriptRef.current = marketExchangeScript;
    }, [marketExchangeScript]);

    useEffect(() => {
      if (prevTabRef.current !== marketActiveTab) {
        isFirstUpdateAfterTabChange.current = true;
      }

      prevTabRef.current = marketActiveTabRef.current;
      marketActiveTabRef.current = marketActiveTab;
    }, [marketActiveTab]);

    const columns = useMemo(() => {
      const colMap = Object.fromEntries(
        DEFAULT_COLUMNS.map((col) => [col.field, col]),
      );

      const userColumns =
        marketWatchColumns?.filter(
          (col) => col.isActive && col.field !== "favourite",
        ) || [];

      const mappedUserColumns = userColumns.map((col) => ({
        ...colMap[col.field],
        ...col,
      }));

      const favouriteColumn = {
        ...colMap["favourite"],
        cellClass: "fav-cell",
        cellRenderer: (p) => {
          const isFavourite = marketWatchScripts?.some(
            (x) => x.ScriptToken === p.data.ScriptToken && x.isFavourite,
          );

          return (
            <>
              {p.data.name ? (
                <FavouriteStar
                  isFavourite={isFavourite}
                  handleClick={() => handleFavourite({ rowData: p?.data })}
                />
              ) : (
                <></>
              )}
            </>
          );
        },
      };

      return [favouriteColumn, ...mappedUserColumns];
    }, [marketWatchColumns, marketWatchScripts]);

    // exclude expire script from store marketWatchScripts
    useEffect(() => {
      if (marketActiveTab === "all" && marketWatchScripts?.length) {
        const filtered = marketWatchScripts.filter((item) => {
          const match = marketFilterScript.find(
            (f) => f.ScriptToken?.toString() === item.ScriptToken?.toString(),
          );

          // when its match compare date only
          if (match?.ExpiryDate) {
            const formattedExpiryDate = dateTimeFormat({
              dateStr: match.ExpiryDate,
            }).dateYMD;

            return formattedExpiryDate >= dateYMD;
          }

          return true;
        });

        // Only update Redux if actual change
        if (filtered.length !== marketWatchScripts.length) {
          dispatch(marketAction.setMarketWatchScripts(filtered));
        }
      }
    }, [marketActiveTab, marketWatchScripts, marketFilterScript]);

    // on page mount subscribe all available scripts
    useEffect(() => {
      if (connected) {
        if (
          marketActiveTab === null ||
          marketActiveTab === undefined ||
          marketActiveTab === "" ||
          marketActiveTab === "all"
        ) {
          if (marketWatchScripts?.length === 0) return;
          // subscribe all custom script
          const tokenString = marketWatchScripts
            .map((x) => x.ScriptToken)
            .join(",");
          send("SubscribeExchangeFull", tokenString);
          send("SubscribeExchangeUpdated", tokenString);
        } else {
          send("SubscribeExchangeWise", marketActiveTab);
        }
      }
    }, [marketWatchScripts, connected, marketActiveTab]);

    useEffect(() => {
      if (
        marketWatchScripts?.length === 0 ||
        marketFilterScript?.length === 0 ||
        marketActiveTabRef.current.toLowerCase() !== "all"
      ) {
        return;
      }

      const currentTokens = new Set(
        rowData.map((item) => item.ScriptToken?.toString()),
      );

      const newItems = marketWatchScripts
        .filter((item) => !currentTokens.has(item.ScriptToken?.toString()))
        .map((item) => {
          const scriptToken = item.ScriptToken?.toString();

          // Match from filter script for name, exchange, expiry
          const match = marketFilterScript.find(
            (f) => f.ScriptToken?.toString() === scriptToken,
          );

          const newObj = newScriptObject({
            scriptToken,
            name: match?.Name,
            exchange: match?.ExchangeName,
            expiry: stringtoDDMMYYYY(match?.ExpiryDate),
          });

          return {
            ...newObj,
            ...item, // include additional props from marketWatchScripts
            _id: scriptToken,
          };
        });

      if (newItems.length > 0) {
        setRowData((prev) => [...prev, ...newItems]);
      }
    }, [marketWatchScripts, marketFilterScript, marketActiveTab]);

    useEffect(() => {
      const interval = setInterval(() => {
        if (updateQueue.current.length === 0) return;

        const updates = [...updateQueue.current];
        updateQueue.current = [];

        // Only use allowedTokens when active tab is null/undefined/empty/all
        let allowedTokens = null;
        if (
          marketActiveTab === null ||
          marketActiveTab === undefined ||
          marketActiveTab === "" ||
          marketActiveTab.toLowerCase() === "all"
        ) {
          allowedTokens = new Set(
            marketWatchScriptsRef.current.map((item) =>
              item.ScriptToken?.toString(),
            ),
          );
        } else {
          allowedTokens = new Set(
            marketExchangeScriptRef.current.map((item) =>
              item.ScriptToken?.toString(),
            ),
          );
        }

        setRowData((prev) => {
          const map = new Map(prev.map((item) => [item.ScriptToken, item]));

          updates.forEach((row) => {
            const token = row.ScriptToken?.toString();

            // If allowedTokens is defined, filter updates
            if (allowedTokens && !allowedTokens.has(token)) return;

            const existing = map.get(token) || {};
            map.set(token, { ...existing, ...row });
          });

          return Array.from(map.values());
        });
      }, 100);

      return () => clearInterval(interval);
    }, [marketActiveTab]);

    const handleFullFeed = (message) => {
      try {
        const json = JSON.parse(decryptMsgData(message));
        const scriptToken = json.instrument_token?.toString();

        // If modal is not open, unsubscribe from full feed
        if (!modalRef.current?.isOpen) {
          send("UnsubscribeExchangeFull", scriptToken);
        }

        // Find matching script info from filterScriptRef
        const matchScript = filterScriptRef.current?.find(
          (script) => script.ScriptToken?.toString() === scriptToken,
        );

        // Push the enriched data into the queue
        updateQueue.current.push({
          ...json,
          _id: scriptToken,
          ScriptToken: json.instrument_token,
          name: matchScript?.Name || "",
          exchange: matchScript?.ExchangeName,
          expiry: stringtoDDMMYYYY(matchScript?.ExpiryDate),
        });
      } catch (err) {
        console.log("err");
        console.error("Error parsing full feed:", err);
      }
    };

    const handleUpdateFeed = (message) => {
      try {
        const json = JSON.parse(decryptMsgData(message));

        // Early exit if not part of Redux
        if (
          !marketWatchScriptsRef.current.some(
            (script) =>
              script.ScriptToken?.toString() ===
              json.instrument_token.toString(),
          )
        ) {
          return;
        }

        // Check if json.instrument_token exists in the marketWatchScriptsRef.current array
        const scriptExists = marketWatchScriptsRef.current.some(
          (script) => script.ScriptToken === json.instrument_token.toString(),
        );

        if (scriptExists) {
          updateQueue.current.push({
            ...json,
            _id: json.instrument_token.toString(),
            ScriptToken: json.instrument_token,
          });
        } else {
          console.log(
            `Instrument token ${json.instrument_token} not found in marketWatchScriptsRef. Skipping update.`,
          );
        }
      } catch (err) {
        console.error("Error parsing update feed:", err);
      }
    };

    const handleUnsubscribe = () => {
      if (
        (marketActiveTab === null ||
          marketActiveTab === undefined ||
          marketActiveTab === "" ||
          marketActiveTab === "all") &&
        marketWatchScripts?.length > 0
      ) {
        const tokenString = marketWatchScriptsRef.current
          .map((x) => x.ScriptToken?.toString())
          .filter(Boolean)
          .join(",");
        if (tokenString) {
          send("UnsubscribeExchangeUpdated", tokenString);
        }
      }
    };

    const handleKeyActions = ({ row, action, updatedRows }) => {
      const token = row?.ScriptToken;
      // if (!token) return;

      if (action === "delete") {
        // Remove from Redux store array marketWatchScripts
        const updatedTokens = marketWatchScripts.filter(
          (item) => item.ScriptToken.toString() !== token.toString(),
        );

        // Dispatch updated array
        dispatch(marketAction.setMarketWatchScripts(updatedTokens));

        // Send unsubscribe to SignalR
        send("UnsubscribeExchangeUpdated", token);

        // Remove from local rowData state
        setRowData((prev) =>
          prev.filter(
            (item) => item.ScriptToken.toString() !== token.toString(),
          ),
        );
      } else if (action === "space") {
        const generateId = randomFiveDigitString();
        const newObj = newScriptObject({ scriptToken: generateId });

        setRowData((prevArr) => {
          const index = prevArr.findIndex(
            (item) => item.ScriptToken?.toString() === token.toString(),
          );

          if (index === -1) return prevArr;

          const newArr = [...prevArr];
          newArr.splice(index + 1, 0, newObj);

          // Also update Redux store with same insertion
          const currentTokens = [...marketWatchScripts];

          const currentIndex = currentTokens.findIndex(
            (item) => item.ScriptToken?.toString() === token.toString(),
          );

          if (currentIndex === -1) {
            currentTokens.push(newObj); // fallback append
          } else {
            currentTokens.splice(currentIndex + 1, 0, newObj);
          }

          dispatch(marketAction.setMarketWatchScripts(currentTokens));

          return newArr;
        });
      } else if (action === "clearAll") {
        // Dispatch updated array
        dispatch(marketAction.setMarketWatchClearScript([]));

        // Remove from local rowData state
        setRowData([]);

        // Clear Redux store marketWatchScripts
        const marketScriptString = marketWatchScripts
          .map((script) => script.ScriptToken)
          .toString();
        // Send unsubscribe to SignalR
        send("UnsubscribeExchangeUpdated", marketScriptString);
      } else if (action === "rowReorder") {
        // Handle row reordering
        // Save new order tokens
        // const newOrder = updatedRows.map((item) => item.ScriptToken);
        // customRowOrderRef.current = newOrder;

        // // Reorder local rowData state
        // setRowData((prevData) => {
        //   const rowMap = new Map(prevData.map((row) => [row.ScriptToken, row]));
        //   const reordered = newOrder
        //     .map((token) => rowMap.get(token))
        //     .filter(Boolean);

        //   // Update Redux store with reordered rows
        //   dispatch(marketAction.setMarketWatchScripts(reordered));

        //   return reordered;
        // });

        // Save new order of ScriptTokens
        // const newOrder = updatedRows.map((item) => item.ScriptToken);
        // customRowOrderRef.current = newOrder;
        // console.log("newOrder => ", newOrder);

        // // Reorder using full data from Redux (or its ref)
        // const fullDataMap = new Map(
        //   marketWatchScriptsRef.current.map((item) => [item.ScriptToken, item])
        // );
        // console.log("fullDataMap => ", fullDataMap);
        // const reordered = newOrder.map((token) => {
        //   return fullDataMap.get(token);
        // })
        // .filter(Boolean);
        // console.log("reordered => ", reordered);

        // Update Redux store with reordered full rows
        dispatch(marketAction.setMarketWatchScripts(updatedRows));

        // Update local rowData as well
        setRowData(updatedRows);
      } else if (action === "info") {
        dispatch(
          uiActions.setCanvasOption({
            isOpen: true,
            rowData: row,
            name: "info",
          }),
        );
      } else if (action === "view chart") {
        dispatch(
          uiActions.setCanvasOption({
            isOpen: true,
            rowData: row,
            name: "chart",
          }),
        );
      }
    };

    // Whenever columns reorder, update Redux store
    const handleColumnMoved = useCallback(
      (newColDefs) => {
        if (!Array.isArray(newColDefs) || newColDefs.length === 0) return;

        const newFieldOrder = newColDefs.map((col) => col.field);

        // Create a map for quick access to original columns
        const colMap = new Map(
          marketWatchColumns.map((col) => [col.field, col]),
        );

        // Reorder the moved fields
        const movedColumns = newFieldOrder
          .map((field) => colMap.get(field))
          .filter(Boolean);

        // Append the untouched ones in their original order
        const remainingColumns = marketWatchColumns.filter(
          (col) => !newFieldOrder.includes(col.field),
        );

        // Merge moved + remaining
        const reorderedColumns = [...movedColumns, ...remainingColumns];

        dispatch(marketAction.setMarketWatchColumns(reorderedColumns));
      },
      [dispatch, marketWatchColumns],
    );

    // favourite scripts handler
    const handleFavourite = ({ rowData }) => {
      // check script is exist or not
      // if not matched just add object script token with favourite key
      // if matched then first find index of mathing object and update whole update
      const existingIndex = marketWatchScriptsRef.current.findIndex(
        (x) => x?.ScriptToken.toString() === rowData.ScriptToken.toString(),
      );

      let updatedList = [...marketWatchScriptsRef.current];

      if (existingIndex < 0) {
        // Not found, add new
        updatedList.push({
          ScriptToken: rowData.ScriptToken,
          isFavourite: true,
        });
      } else {
        // Found, toggle or update favourite
        const existingItem = updatedList[existingIndex];
        updatedList[existingIndex] = {
          ...existingItem,
          isFavourite: !existingItem.isFavourite,
        };
      }

      dispatch(marketAction.setMarketWatchScripts(updatedList));
    };

    useImperativeHandle(ref, () => ({
      gridRef: gridRef,
      getRowData: rowData,
      setRowData: (data) => setRowData(data),
      handleKeyActions: handleKeyActions,
      handleColumnMoved: handleColumnMoved,
      customRowOrderRef: customRowOrderRef,
      marketWatchScriptsRef: marketWatchScriptsRef,
      handleFavourite: handleFavourite,
      useChat: {
        connected,
        on,
        off,
        send,
      },
    }));

    return (
      <AgGridDataTable
        ref={gridRef}
        rowData={rowData}
        colDefs={columns}
        handleKeyActions={handleKeyActions}
        onColumnMoved={handleColumnMoved}
        contextRef={contextRef}
      />
    );
  },
);

export default MarketWatchTable;
